import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { AnnotationEntry } from '@geosoft/viewer-sdk';
import { createAnnotationBridgeStore } from '@geosoft/viewer-sdk';
import { useViewerStore } from '@/state/useViewerStore';
import { usePluginStore } from '@/state/usePluginStore';
import { AnnotationPanel, persistPointAnnotation } from '@/plugins/annotation';
import { ExportPanel } from '@/plugins/export';
import { MeasurementPanel } from '@/plugins/measurement';
import { ModelTreePanel } from '@/plugins/model-tree';
import { ProfilePanel } from '@/plugins/profile';
import { ViewshedPanel } from '@/plugins/viewshed';
import { TerrainAnalysisPanel } from '@/plugins/terrain-analysis';
import { PluginToolbar } from './PluginToolbar';

type PluginMode = 'measurement' | 'annotation' | 'model-tree' | 'export' | 'profile' | 'viewshed' | 'terrain-analysis';

const annotationBridgeStore = createAnnotationBridgeStore();

export function PluginDock() {
  const [mode, setMode] = useState<PluginMode>('measurement');
  const [annotations, setAnnotations] = useState<AnnotationEntry[]>([]);
  const measurePoints = usePluginStore((s) => s.transient.measurement.vertices);
  const updateMeasurement = usePluginStore((s) => s.updateMeasurementTransient);
  const setMeasurePoints = useCallback(
    (fn: (prev: Array<[number, number]>) => Array<[number, number]>) => {
      updateMeasurement({ vertices: fn(measurePoints) });
    },
    [measurePoints, updateMeasurement],
  );
  const [position, setPosition] = useState({ x: 12, y: 12 });
  const dragRef = useRef<{ active: boolean; pointerId: number | null; dx: number; dy: number }>({
    active: false,
    pointerId: null,
    dx: 0,
    dy: 0,
  });

  const selection = useViewerStore((s) => s.selection);
  const visibleLayerIds = useViewerStore((s) => s.visibleLayerIds);
  const time = useViewerStore((s) => s.time);
  const { activePluginId, engineType, engineReady } = usePluginStore();

  // Sync active plugin with mode
  useEffect(() => {
    if (activePluginId && activePluginId !== 'export') {
      setMode(activePluginId as PluginMode);
    }
  }, [activePluginId]);

  const selectedPoint = useMemo(() => {
    if (!selection?.point || selection.point.length < 2) return null;
    const [lon, lat] = selection.point;
    if (typeof lon !== 'number' || typeof lat !== 'number') return null;
    return [lon, lat] as [number, number];
  }, [selection]);

  const addMeasurePoint = () => {
    if (!selectedPoint) return;
    setMeasurePoints((prev) => [...prev, selectedPoint]);
  };

  // Auto-add measurement point on map click when measurement mode is active.
  const prevSelectionRef = useRef<readonly [number, number] | null>(null);
  useEffect(() => {
    if (mode !== 'measurement') return;
    if (!selectedPoint) return;
    if (
      prevSelectionRef.current &&
      prevSelectionRef.current[0] === selectedPoint[0] &&
      prevSelectionRef.current[1] === selectedPoint[1]
    ) {
      return;
    }
    prevSelectionRef.current = selectedPoint;
    setMeasurePoints((prev) => [...prev, selectedPoint]);
  }, [selectedPoint, mode, setMeasurePoints]);

  const addAnnotation = async () => {
    if (!selectedPoint) return;
    const annotation = await persistPointAnnotation(annotationBridgeStore, {
      lonLat: selectedPoint,
      tenantId: 'demo-tenant',
      projectId: 'dubai-vertical-slice',
      actor: 'agent:f7-annotation',
    });
    setAnnotations((prev) => [...prev, annotation]);
  };

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (!dragRef.current.active) return;
      setPosition({
        x: Math.max(8, event.clientX - dragRef.current.dx),
        y: Math.max(8, event.clientY - dragRef.current.dy),
      });
    };
    const onPointerUp = (event: PointerEvent) => {
      if (dragRef.current.pointerId !== event.pointerId) return;
      dragRef.current.active = false;
      dragRef.current.pointerId = null;
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, []);

  const onHeaderPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    dragRef.current.active = true;
    dragRef.current.pointerId = event.pointerId;
    dragRef.current.dx = event.clientX - position.x;
    dragRef.current.dy = event.clientY - position.y;
  };

  return (
    <aside className="plugin-dock" aria-label="Plugin tools" style={{ left: position.x, top: position.y }}>
      <header onPointerDown={onHeaderPointerDown}>
        <h3>Plugins</h3>
        <div className="plugin-engine-status">
          <span 
            className={`engine-indicator ${engineReady ? 'ready' : 'not-ready'}`}
            title={engineType ? `${engineType} engine ${engineReady ? 'ready' : 'loading'}` : 'No engine'}
          >
            <span className="material-symbols-outlined">
              {engineReady ? 'check_circle' : 'hourglass_empty'}
            </span>
            {engineType ?? 'none'}
          </span>
        </div>
      </header>
      
      <PluginToolbar />
      <div className="plugin-body">
        {mode === 'measurement' && (
          <MeasurementPanel
            selectedPoint={selectedPoint}
            measurePoints={measurePoints}
            annotations={annotations}
            onAddPoint={addMeasurePoint}
            onReset={() => updateMeasurement({ vertices: [] })}
          />
        )}
        {mode === 'annotation' && (
          <AnnotationPanel
            selectedPoint={selectedPoint}
            annotations={annotations}
            onAddPoint={() => {
              void addAnnotation();
            }}
          />
        )}
        {mode === 'model-tree' && (
          <ModelTreePanel
            visibleLayerIds={[...visibleLayerIds.values()]}
            annotations={annotations}
          />
        )}
        {mode === 'export' && (
          <ExportPanel
            visibleLayerIds={[...visibleLayerIds.values()]}
            time={time}
            annotations={annotations}
            measurePoints={measurePoints}
          />
        )}
        {mode === 'profile' && (
          <ProfilePanel annotations={annotations} />
        )}
        {mode === 'viewshed' && (
          <ViewshedPanel annotations={annotations} />
        )}
        {mode === 'terrain-analysis' && (
          <TerrainAnalysisPanel annotations={annotations} />
        )}
      </div>
    </aside>
  );
}
